import React from "react";
import { api } from "@tripian/core";

// Session boyunca loglanan hataları tut (aynı hata tekrar loglanmaz)
const loggedErrors = new Set<string>();

const logError = (error: Error) => {
  if (error instanceof DOMException || error.message.startsWith("Minified React error")) return;

  const errorKey = `${error.message}-${window.location.pathname}`;

  // Bu hata zaten bu session'da loglandıysa tekrar loglama
  if (loggedErrors.has(errorKey)) {
    return;
  }

  loggedErrors.add(errorKey);

  const simplifiedLog = {
    platform: "web",
    type: "ERROR",
    endpoint: window.location.href,
    path: window.location.pathname,
    api_base_url: window.tconfig?.TRIPIAN_API_URL || "",
    api_key: window.tconfig?.TRIPIAN_API_KEY || "",
    message: error.message,
    stack_preview: error.stack?.split("\n").slice(0, 3).join(" | "),
  };

  console.log("Error Log:", simplifiedLog);

  if (process.env.NODE_ENV === "development") return;

  try {
    if (api) {
      api.log("ErrorHandling: " + error.message, simplifiedLog);
    }
  } catch (err) {
    console.warn("Failed to send error log:", err);
  }
};

// Sessiz retry sınırı: hata kalıcıysa (ör. store'da bozuk state) sonsuz çökme döngüsüne girmesin
const MAX_SILENT_RETRIES = 3;
const RETRY_WINDOW_MS = 10000;
let retryCount = 0;
let lastErrorAt = 0;

const ErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
}> = ({ error, resetErrorBoundary }) => {
  // Hata logla (aynı hata session boyunca tekrar loglanmaz)
  logError(error);

  // Bir süre sorunsuz çalıştıysa sayaç sıfırlanır, arada bir olan geçici hatalar sessiz kalır
  const [canRetry] = React.useState(() => {
    const now = Date.now();
    if (now - lastErrorAt > RETRY_WINDOW_MS) retryCount = 0;
    lastErrorAt = now;
    retryCount += 1;
    return retryCount <= MAX_SILENT_RETRIES;
  });

  React.useEffect(() => {
    if (!canRetry) return undefined;
    // Küçük delay ile retry yap (CPU'yu boğmasın)
    const timer = setTimeout(() => {
      resetErrorBoundary();
    }, 100);

    return () => clearTimeout(timer);
  }, [canRetry, resetErrorBoundary]);

  // Retry hakkı varken hiçbir şey gösterme - sessiz retry
  if (canRetry) return null;

  // Translation/store'a bağımlı olmayan sade fallback; bunlar da çökmüş olabilir
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "1rem", textAlign: "center", fontFamily: "sans-serif" }}>
      <p style={{ marginBottom: "1rem" }}>Something went wrong. Please refresh the page.</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ padding: "0.5rem 1.5rem", borderRadius: "9999px", border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}
      >
        Refresh
      </button>
    </div>
  );
};

// Geriye uyumluluk için myErrorHandler'ı da export et
const myErrorHandler = (error: Error, _info: React.ErrorInfo) => {
  logError(error);
};

export const ErrorHandling = {
  ErrorFallback,
  myErrorHandler,
};